import * as React from 'react';
import { connect } from 'react-redux';

import Page from 'components/page/Page';

import InfluencerEditProfile from 'modules/influencers/components/influencer-edit-profile/InfluencerEditProfile';
import InfluencerEditProfileLightweight from 'modules/influencers/components/influencer-edit-profile/lightweight/InfluencerEditProfileLightweight';

import { fetchInfluencerAudienceData, fetchInfluencerProfile, resetInfluencerProfile } from 'modules/influencers/reducers/influencerProfile';
import { updateLoginState } from 'components/external-integration/facebook/button';

import { t } from 'utils/language';
import { isAdmin } from 'utils/account';
import { isBrandsFocusedOrganization } from 'utils/organization';
import { getExpiredAnalyticsDataForInfluencer } from 'utils/influencers';

import type {
  Account,
  ChannelAnalyticsScreenShotData,
  InfluencerProfileForm,
  Organization,
  Params,
  Role
} from 'types';

type StateProps = {
  profile: InfluencerProfileForm,
  currentAccount: Account,
  roles: Array<Role>,
  currentOrganization: Organization
};

type DispatchProps = {
  dispatchFetchProfile: (profileId: number) => void,
  dispatchResetProfile: () => void,
  dispatchUpdateLoginState: (profileId: number) => void,
  dispatchFetchInfluencerAudienceData: (a: number) => Promise<Array<ChannelAnalyticsScreenShotData>>
};

type OwnProps = {
  params: Params
};

type Props = StateProps & DispatchProps & OwnProps;

const InfluencerEditProfilePage = ({
  params,
  profile,
  currentAccount,
  currentOrganization,
  roles,
  dispatchFetchProfile,
  dispatchResetProfile,
  dispatchUpdateLoginState,
  dispatchFetchInfluencerAudienceData
}: Props): JSX.Element => {
  const [expiredAnalyticsDataForInfluencer, setExpiredAnalyticsDataForInfluencer] = React.useState({});
  const isUserAdmin = isAdmin(currentAccount, roles);
  const isBrand = isBrandsFocusedOrganization(currentOrganization);
  const profileId = Number(params.id) ?? profile.id ?? -1;

  React.useEffect(() => {
    if (profileId) {
      dispatchFetchProfile(profileId);
      dispatchFetchInfluencerAudienceData(profileId)
        .then(data => {
          const expiredAnalytics = getExpiredAnalyticsDataForInfluencer(data, true);
          setExpiredAnalyticsDataForInfluencer(expiredAnalytics);
        });
    }

    if (isBrand && isUserAdmin) {
      dispatchUpdateLoginState(profileId);
    }

    return () => {
      dispatchResetProfile();
    };
  }, [profileId]);

  return (
    <Page
      documentTitle={t('edit_profile_page')}
    >
      {
        profile.isLightweight ?
          <InfluencerEditProfileLightweight
            key={`edit-profile-lightweight-${profileId}`}
            profileId={profileId}
            isAdmin={isUserAdmin}
          /> :
          <InfluencerEditProfile
            key={`edit-profile-${profileId}`}
            profile={profile}
            isAdmin={isUserAdmin}
            expiredAnalyticsDataForInfluencer={expiredAnalyticsDataForInfluencer}
          />
      }
    </Page>
  );
};

const mapStateToProps = ({
  influencerProfile,
  account,
  organizations
}): StateProps => ({
  profile: influencerProfile.profile,
  currentAccount: account.currentAccount,
  roles: account.roles,
  currentOrganization: organizations.currentOrganization
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchProfile: (profileId) => (
    dispatch(fetchInfluencerProfile(profileId))
  ),
  dispatchResetProfile: () => (
    dispatch(resetInfluencerProfile())
  ),
  dispatchUpdateLoginState: (profileId: number) => (
    dispatch(updateLoginState(profileId))
  ),
  dispatchFetchInfluencerAudienceData: (profileId: number) => (
    dispatch(fetchInfluencerAudienceData(profileId))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerEditProfilePage);
